import React, { useContext } from 'react';
import * as R from 'ramda';

import { TaskField } from '@atom/types/task';
import {
  InvalidTaskFields,
  TaskRequiredValidation,
  WorkOrderDetailType,
  WorkOrderField,
} from '@atom/types/work';
import { removeFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  getCascadeValidationTarget,
  getFieldTreeIds,
  getInvalidFieldId,
  getRootFieldById,
  isRequiredCompleteFieldInvalid,
  isRequiredOnCreateFieldInvalid,
  validateTaskRequiredFields,
} from '@atom/utilities/workOrderFieldUtilities';
import {
  CLOSED_STATUS_ID,
  COMPLETED_STATUS_ID,
  isWorkOrderCompletedOrAbove,
} from '@atom/utilities/workOrderStatusUtilities';

import BulkContext, { buildTabs, Tab } from '../BulkContext';

// Fields marked `required` become required once the work order is being
// created directly into a Completed or Closed status.
const isRequiredOnCompleteStatus = (statusId: string) =>
  statusId === COMPLETED_STATUS_ID || statusId === CLOSED_STATUS_ID;

const isWorkInfoFieldInvalid = (
  field: WorkOrderField,
  workOrderDetail: WorkOrderDetailType,
) =>
  isRequiredOnCreateFieldInvalid(field) ||
  (isRequiredOnCompleteStatus(workOrderDetail.statusId) &&
    field.required &&
    isRequiredCompleteFieldInvalid(getCascadeValidationTarget(field)));

// Validate the fields of the 'general' tab.
const validateGeneralRequiredFields = (
  workOrderDetail: WorkOrderDetailType,
): TaskRequiredValidation => {
  const fields = [];

  // Only validate these fields when they are visible:
  if (isWorkOrderCompletedOrAbove(workOrderDetail.statusId)) {
    fields.push('completionDate');
    fields.push('completedBy');
  }

  if (workOrderDetail.statusId === CLOSED_STATUS_ID) {
    fields.push('closedDate');
    fields.push('closedBy');
  }

  const invalidFields = fields.reduce((acc, field) => {
    return isNilOrEmpty(workOrderDetail[field]) ? [...acc, field] : acc;
  }, []);

  return {
    isValid: invalidFields.length === 0,
    invalidTaskFields: {
      general: new Set(invalidFields),
    },
  };
};

// Validate the custom WorkOrderField inputs on the 'work_info' tab.
const validateWorkInfoRequiredFields = (
  workOrderDetail: WorkOrderDetailType,
): TaskRequiredValidation => {
  const invalidFieldIds = (workOrderDetail.fields || []).reduce(
    (acc, field) =>
      isWorkInfoFieldInvalid(field, workOrderDetail)
        ? [...acc, getInvalidFieldId(field)]
        : acc,
    [],
  );

  return {
    isValid: invalidFieldIds.length === 0,
    invalidTaskFields: {
      work_info: new Set(invalidFieldIds),
    },
  };
};

const validateTab = (
  workOrderDetail: WorkOrderDetailType,
  tabId: string,
): TaskRequiredValidation =>
  tabId === 'general'
    ? validateGeneralRequiredFields(workOrderDetail)
    : tabId === 'work_info'
      ? validateWorkInfoRequiredFields(workOrderDetail)
      : validateTaskRequiredFields(workOrderDetail, tabId);

// Narrows each tab's existing errors to those still invalid for the current
// work order (e.g. after a status change). Never adds new errors, and drops
// tabs left with none.
export const pruneResolvedInvalidFields = (
  workOrderDetail: WorkOrderDetailType,
  invalidFields: InvalidTaskFields,
): InvalidTaskFields =>
  Object.entries(invalidFields).reduce((acc, [tabId, fieldIds]) => {
    const stillInvalidIds =
      validateTab(workOrderDetail, tabId).invalidTaskFields[tabId] ||
      new Set<string>();
    const remainingIds = Array.from(fieldIds).filter(id =>
      stillInvalidIds.has(id),
    );

    return remainingIds.length === 0
      ? acc
      : { ...acc, [tabId]: new Set(remainingIds) };
  }, {});

const countInvalidFieldIds = (invalidFields: InvalidTaskFields) =>
  R.sum(Object.values(invalidFields).map(fieldIds => fieldIds.size));

export const validateAll = (
  workOrderDetail: WorkOrderDetailType,
  invalidTabs: Set<string>,
  invalidFields: InvalidTaskFields,
  setInvalidTabs: React.Dispatch<React.SetStateAction<Set<string>>>,
  setInvalidFields: React.Dispatch<React.SetStateAction<InvalidTaskFields>>,
  setTab: React.Dispatch<React.SetStateAction<Tab>>,
) => {
  const tabs = buildTabs(workOrderDetail.tasks);
  let isAllValid = true;
  // Create a local working copy of invalidTabs and invalidFields,
  // since if we call set() on each loop iteration, the value
  // won't be actually set on the state.
  let localInvalidTabs = Array.from(invalidTabs);
  let localInvalidFields = { ...invalidFields };

  // Validate all tabs:
  tabs.forEach(tab => {
    const validation = validateTab(workOrderDetail, tab.id);

    if (validation.isValid) {
      localInvalidTabs = [...localInvalidTabs].filter(
        invalidTab => invalidTab !== tab.id,
      );
      localInvalidFields = { ...R.omit([tab.id], localInvalidFields) };
    } else {
      localInvalidTabs = [...localInvalidTabs, tab.id];
      localInvalidFields = {
        ...localInvalidFields,
        ...validation.invalidTaskFields,
      };

      // Switch to the step with errors (only the first time):
      if (isAllValid) {
        setTab(tab);
        isAllValid = false;
      }
    }
  });

  // And the end, set the invalid tabs and fields values:
  setInvalidTabs(new Set([...localInvalidTabs]));
  setInvalidFields({ ...localInvalidFields });

  if (isAllValid) {
    return Promise.resolve();
  }

  return Promise.reject();
};

const setInvalid = (
  tab: string,
  field: string,
  isValid: boolean,
  invalidTabs: Set<string>,
  invalidFields: InvalidTaskFields,
  setInvalidTabs: React.Dispatch<React.SetStateAction<Set<string>>>,
  setInvalidFields: React.Dispatch<React.SetStateAction<InvalidTaskFields>>,
) => {
  if (isValid) {
    const updatedFieldIds = removeFromSet(
      invalidFields[tab] || new Set([]),
      field,
    );

    const updatedInvalidFields =
      updatedFieldIds.size === 0
        ? R.omit([tab], invalidFields)
        : {
            ...invalidFields,
            [tab]: updatedFieldIds,
          };

    setInvalidFields(updatedInvalidFields);

    if (updatedFieldIds.size === 0) {
      // Remove tab from invalidTabs if there are no errors left in this tab.
      setInvalidTabs(
        new Set(
          Array.from(invalidTabs).filter(invalidTab => invalidTab !== tab),
        ),
      );
    }
  } else {
    setInvalidFields({
      ...invalidFields,
      [tab]: new Set([...(invalidFields[tab] || []), field]),
    });
    setInvalidTabs(new Set([...invalidTabs, tab]));
  }
};

const isGeneralFieldValid = (
  field: string,
  workOrderDetail: WorkOrderDetailType,
) => !isNilOrEmpty(workOrderDetail[field]);

export const useWorkOrderValidation = () => {
  const { invalidTabs, invalidFields, setInvalidTabs, setInvalidFields } =
    useContext(BulkContext);

  // These methods run validation on a specific single field, if the validation
  // succeeds, any previous validation error is cleared.
  //
  // If it fails, we don't set the error, we only do full from validation
  // when we click on CREATE (i.e. calling validateAll()).
  const setTabInvalidFieldIds = (tab: string, fieldIds: string[]) => {
    setInvalidFields(
      fieldIds.length === 0
        ? R.omit([tab], invalidFields)
        : { ...invalidFields, [tab]: new Set(fieldIds) },
    );

    if (fieldIds.length === 0) {
      setInvalidTabs(
        new Set(
          Array.from(invalidTabs).filter(invalidTab => invalidTab !== tab),
        ),
      );
    }
  };

  // Clears any error on the (possibly cascading) field containing fieldId.
  // Errors are only raised by validateAll(), so newly revealed cascade levels
  // are not flagged until the user tries to create again.
  const clearFieldTreeErrors = <T extends WorkOrderField | TaskField>(
    tab: string,
    fieldId: string,
    fields: T[],
  ) => {
    const rootField = getRootFieldById(fieldId, fields);
    const invalidFieldIds = Array.from(invalidFields[tab] || []);

    if (!rootField) {
      return;
    }

    const treeIds = new Set(getFieldTreeIds(rootField));
    const otherInvalidFieldIds = invalidFieldIds.filter(id => !treeIds.has(id));

    if (otherInvalidFieldIds.length === invalidFieldIds.length) {
      return;
    }

    setTabInvalidFieldIds(tab, otherInvalidFieldIds);
  };

  return {
    // Validate a single field from the 'general' step.
    validateField: (workOrderDetail, field) => {
      const isValid = isGeneralFieldValid(field, workOrderDetail);
      if (isValid) {
        setInvalid(
          'general',
          field,
          isValid,
          invalidTabs,
          invalidFields,
          setInvalidTabs,
          setInvalidFields,
        );
      }
    },
    // Clears errors on every tab that no longer apply once the status
    // changes, e.g. completed/closed fields on the general tab and fields
    // `required` only on Completed/Closed on the work info and task tabs.
    // Computed in one pass so tabs don't overwrite each other's updates.
    revalidateStatusChange: (workOrderDetail: WorkOrderDetailType) => {
      const updatedInvalidFields = pruneResolvedInvalidFields(
        workOrderDetail,
        invalidFields,
      );
      // Pruning only removes ids, so an unchanged count means nothing cleared.
      if (
        countInvalidFieldIds(updatedInvalidFields) ===
        countInvalidFieldIds(invalidFields)
      ) {
        return;
      }

      const resolvedTabs = R.difference(
        R.keys(invalidFields),
        R.keys(updatedInvalidFields),
      );

      setInvalidFields(updatedInvalidFields);
      setInvalidTabs(
        new Set(
          Array.from(invalidTabs).filter(tab => !resolvedTabs.includes(tab)),
        ),
      );
    },
    // Validate a single field from the 'work_info' step.
    validateWorkInfoField: (fieldId, workOrderDetail) =>
      clearFieldTreeErrors('work_info', fieldId, workOrderDetail.fields || []),
    // Validate a single field from any task step.
    validateTaskField: (fieldId, task) =>
      clearFieldTreeErrors(task.id, fieldId, task.fields || []),
  };
};
